import { Injectable } from '@angular/core';
interface StoredKey {
  name: string;
  key: CryptoKeyPair;
}


interface JWK {
  kty: string;
  crv: string;
  x: string;
  y: string;
  kid?: string;
  use?: string;
  alg?: string;
  key_ops?: string[];
  ext?: boolean;
}


interface JWKSResponse {
  keys: JWK[];
}


@Injectable({
  providedIn: 'root'
})
export class KeyService {
  private db!: IDBDatabase;
  jwk!: JWKSResponse;
  serverKeyRes!: JWK;


  constructor() {
    this.initDB();
  }


  initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('crypto-keys', 2);


      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        db.createObjectStore('keys', { keyPath: 'name' });
      };


      request.onsuccess = (event: Event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        this.generateKeys().catch(console.error);
        resolve(this.db);
      };


      request.onerror = (event: Event) => {
        reject((event.target as IDBOpenDBRequest).error);
      };
    });
  }


  private async generateKeys(): Promise<void> {
    const keyPair = await window.crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveKey', 'deriveBits']);
    const transaction = this.db.transaction(['keys'], 'readwrite');
    const store = transaction.objectStore('keys');
    const data: StoredKey = { name: 'client-key', key: keyPair };
    store.put(data);
  }


  async getClientKey(): Promise<CryptoKeyPair> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['keys'], 'readonly');
      const store = transaction.objectStore('keys');
      const request = store.get('client-key');


      request.onsuccess = () => {
        const result = request.result as StoredKey;
        resolve(result.key);
      };


      request.onerror = () => reject(request.error);
    });
  }


  async getServerPublicKey(jwksUrl: string): Promise<CryptoKey> {
    if (!this.serverKeyRes) {
      const response = await fetch(jwksUrl);
      const jwks: JWKSResponse = await response.json();
      this.jwk = jwks;
      this.serverKeyRes = jwks.keys[0];
    }


    return crypto.subtle.importKey('jwk', this.serverKeyRes, { name: 'ECDH', namedCurve: 'P-256' }, true, []);
  }
}
